import React from "react";
import styled from "styled-components";
import { colors2 } from "oolib";
import { UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";

// Wraps the bar in a column so the legend stacks below it.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Segments = styled.div`
  display: flex;
  gap: 2px;
  width: 100%;
  height: ${({ $height }) => $height}px;
`;

// Sized by flex-grow so the same component works whether the caller passes raw
// counts or pre-computed percentages — both are just proportional weights.
const Segment = styled.div`
  flex: ${({ $weight }) => $weight};
  background: ${({ $color }) => $color};
  border-radius: 2px;

  ${({ $roundedEnds }) =>
    $roundedEnds &&
    `
    &:first-child {
      border-top-left-radius: 7px;
      border-bottom-left-radius: 7px;
    }
    &:last-child {
      border-top-right-radius: 7px;
      border-bottom-right-radius: 7px;
    }
  `}
`;

// space-between spreads the items to the bar's ends: first sits left, last sits
// right, and any middle item centres.
const LegendContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.8rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-direction: ${({ $flip }) => ($flip ? "row-reverse" : "row")};
`;

const LegendDot = styled.span`
  width: 8px;
  height: 12px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LegendText = styled(UI_CAPTION_SEMIBOLD_DF)`
  font-weight: 500;
  color: ${colors2.grey60};
  white-space: nowrap;
`;

// When flipped (the right-most item of a two-segment bar) the dot trails the
// label so it hugs the bar's right end.
function Legend({ color, value, label, flip }) {
  return (
    <LegendItem $flip={flip}>
      <LegendDot $color={color} />
      <LegendText>{`${value}%`}</LegendText>
      <LegendText>{label}</LegendText>
    </LegendItem>
  );
}

const capitalize = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Horizontal stacked sentiment/polarity bar with an optional legend below.
 *
 * @param segments   [{ key, value, color }] in render order; only value > 0 are
 *                   shown, so :first-child / :last-child line up with the
 *                   actually-visible ends.
 * @param height     bar height in px (default 5).
 * @param roundedEnds pill-rounds the outer corners of the first/last segment.
 * @param showLegend renders a dot + "{value}% {Label}" row below the bar (value
 *                   is assumed to be a percentage).
 */
export function SentimentBar({ segments = [], height = 5, roundedEnds = false, showLegend = false }) {
  const visible = segments.filter((s) => s.value > 0);
  if (visible.length === 0) return null;

  return (
    <Container>
      <Segments $height={height}>
        {visible.map((s) => (
          <Segment key={s.key} $weight={s.value} $color={s.color} $roundedEnds={roundedEnds} />
        ))}
      </Segments>
      {showLegend && (
        <LegendContainer>
          {visible.map((s, i) => (
            <Legend
              key={s.key}
              color={s.color}
              value={s.value}
              label={capitalize(s.key)}
              flip={visible.length === 2 && i === visible.length - 1}
            />
          ))}
        </LegendContainer>
      )}
    </Container>
  );
}

export default SentimentBar;
